import { useCallback, useMemo, useState } from "react";
import {
  AccountData,
  Event,
  RequestStatus,
  TicketV2,
  Campaign,
} from "@markit/common.types";
import { eventHasRequestTickets } from "@markit/common.utils";
import { TabPanel } from "../FullEventSubComponents/TabPanel";
import { DashboardListPanel } from "./DashboardListPanel";
import { useLoadUserTicketList } from "../../hooks/useLoadUserTicketList";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import EmptyStateButton from "../../components/Buttons/EmptyStateButton";
import filter from "lodash.filter";
import { EmptyStateFlatlist } from "../EmptyStates/EmptyStateFlatlist";
import { EventRefundModal } from "./DashboardModals/EventRefundModal";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { AutomationItemPreview } from "../DisplayItem/CampaignItem/AutomationItemPreview";
import { CampaignDetails } from "../../redux/slices/campaignSlice";
import FullProfilePreviewModal from "../FollowerProfile/FullProfilePreviewModal";
import ProfileTicketItem, {
  TICKET_FLEX_SECTIONS,
} from "../DisplayItem/ProfileTicketItem";
import { fetchAllTicketUserData } from "../../utils/eventUtils/eventUtils";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { SearchBoxContactsMode } from "../Containers/SearchBox/SearchBoxContactsContainer";
import {
  searchByPhoneNumber,
  searchByPhoneNumberNoResults,
} from "../../utils/phoneUtils";

type OrderListPanelProps = {
  product: Event;
  userTicket: TicketV2 | undefined;
  dashboardValue: number;
  totalGuests: number;
  totalRequested: number;
  totalRefunded: number;
  campaign: Campaign;
  campaignDetail: CampaignDetails;
};

export const OrderListPanel = (props: OrderListPanelProps) => {
  const {
    product,
    userTicket,
    dashboardValue,
    totalGuests,
    totalRequested,
    totalRefunded,
    campaign,
    campaignDetail,
  } = props;
  const { followingAccountData } = useSelector(getAccountState).account;
  const [listValue, setListValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchMode, setSearchMode] = useState<SearchBoxContactsMode>(
    SearchBoxContactsMode.NAME
  );
  const [searchedPhoneUser, setSearchedPhoneUser] = useState<
    AccountData | undefined
  >(undefined);
  const [refundUserItem, setRefundUserItem] = useState<
    { user: AccountData; ticket: TicketV2 } | undefined
  >(undefined);
  const [profilePreviewSelected, setProfilePreviewSelected] =
    useState<AccountData>();
  const [loadingOrderAction, setLoadingOrderAction] = useState(false);
  const [allOrderUserData, setAllOrderUserData] = useState<AccountData[]>([]);

  const styles = {
    sectionContainer: {
      backgroundColor: Colors.WHITE,
      padding: 14,
      borderRadius: 14,
      flex: 1,
    },
  };

  const {
    isFinished: isAttendeeFinished,
    isLoading: isAttendeeLoading,
    fetchedUserData: fullAttendeeAccountData,
    fetchedTicketData: fullAttendeeTicketData,
    loadTicketUsers: loadAttendeeUsers,
    loadMoreTicketUsers: loadMoreAttendeeUsers,
    loadSearchResultsThrottled: loadAttendeeSearchResultsThrottled,
    loadTicketsForUid: loadAttendeeTicketsForUid,
  } = useLoadUserTicketList({
    event: product,
    windowSize: 15,
    ticketType: RequestStatus.ACCEPTED,
    preloadedUserData: allOrderUserData,
  });

  const {
    isFinished: isPendingRequestsFinished,
    isLoading: isPendingRequestsLoading,
    fetchedUserData: fullPendingRequestsAccountData,
    fetchedTicketData: fullPendingRequestsTicketData,
    loadTicketUsers: loadPendingRequestsUsers,
    loadMoreTicketUsers: loadMorePendingRequestsUsers,
    loadSearchResultsThrottled: loadPendingRequestsSearchResultsThrottled,
    loadTicketsForUid: loadPendingRequestsTicketsForUid,
  } = useLoadUserTicketList({
    event: product,
    windowSize: 15,
    ticketType: RequestStatus.PENDING,
    preloadedUserData: allOrderUserData,
  });

  const {
    isFinished: isRefundedFinished,
    isLoading: isRefundedLoading,
    fetchedUserData: fullRefundedAccountData,
    fetchedTicketData: fullRefundedTicketData,
    loadTicketUsers: loadRefundedUsers,
    loadMoreTicketUsers: loadMoreRefundedUsers,
    loadSearchResultsThrottled: loadRefundedSearchResultsThrottled,
    loadTicketsForUid: loadRefundedTicketsForUid,
  } = useLoadUserTicketList({
    event: product,
    windowSize: 15,
    ticketType: RequestStatus.REFUNDED,
    preloadedUserData: allOrderUserData,
  });

  const hasRequestTicket = useMemo(
    () => eventHasRequestTickets(product),
    [product]
  );

  const isRequestListView = useMemo(
    () => listValue === 1 && hasRequestTicket,
    [listValue, hasRequestTicket]
  );

  const isRefundedListView = useMemo(
    () => listValue === (hasRequestTicket ? 2 : 1),
    [hasRequestTicket, listValue]
  );

  useAsyncOnMount(async () => {
    const ticketsUserData = await fetchAllTicketUserData(
      product.id,
      followingAccountData
    );
    setAllOrderUserData(ticketsUserData);

    await Promise.all([
      loadAttendeeUsers(),
      loadPendingRequestsUsers(),
      loadRefundedUsers(),
    ]);
  });

  const containsUser = useCallback((item: AccountData, query: string) => {
    return item.fullName.toLowerCase().includes(query);
  }, []);

  const currentOrdersToShow = useMemo(() => {
    const ordersToShow: TicketV2[] =
      listValue === 0
        ? fullAttendeeTicketData
        : isRequestListView
        ? fullPendingRequestsTicketData
        : fullRefundedTicketData;
    const usersToShow: AccountData[] =
      listValue === 0
        ? fullAttendeeAccountData
        : isRequestListView
        ? fullPendingRequestsAccountData
        : fullRefundedAccountData;

    if (searchTerm === "") {
      return ordersToShow;
    }
    if (searchMode === SearchBoxContactsMode.PHONE) {
      if (!searchedPhoneUser) {
        return [];
      }
      return ordersToShow.filter((t) => t.uid === searchedPhoneUser.uid);
    }
    let userList: AccountData[] = usersToShow;
    userList = filter(userList, (user: AccountData) => {
      return containsUser(user, searchTerm);
    });
    const tickets: TicketV2[] = [];
    for (let i = 0; i < userList.length; i++) {
      tickets.push(...ordersToShow.filter((t) => t.uid === userList[i].uid));
    }

    return tickets;
  }, [
    listValue,
    fullAttendeeTicketData,
    isRequestListView,
    fullPendingRequestsTicketData,
    fullRefundedTicketData,
    fullAttendeeAccountData,
    fullPendingRequestsAccountData,
    fullRefundedAccountData,
    searchTerm,
    searchMode,
    searchedPhoneUser,
    containsUser,
  ]);

  const allGuestAccountData = useMemo(
    () =>
      fullAttendeeAccountData
        .concat(fullPendingRequestsAccountData)
        .concat(fullRefundedAccountData),
    [
      fullAttendeeAccountData,
      fullPendingRequestsAccountData,
      fullRefundedAccountData,
    ]
  );

  const handleSearch = useCallback(
    async (text: string) => {
      setSearchedPhoneUser(undefined);
      const lowerCaseText = text.toLowerCase();
      setSearchTerm(lowerCaseText);
      if (searchMode === SearchBoxContactsMode.NAME) {
        if (text !== "") {
          if (!isRequestListView && !isRefundedListView) {
            loadAttendeeSearchResultsThrottled(lowerCaseText);
          } else if (isRequestListView) {
            loadPendingRequestsSearchResultsThrottled(lowerCaseText);
          } else {
            loadRefundedSearchResultsThrottled(lowerCaseText);
          }
        }
      } else {
        const searchedPhoneUser = await searchByPhoneNumber(
          text,
          product.createdBy,
          followingAccountData
        );
        if (searchedPhoneUser) {
          setSearchedPhoneUser(searchedPhoneUser.user);
          // Targeted query so this person's order shows up even if it hasn't
          // been paginated into the current tab's ticket data yet
          if (!isRequestListView && !isRefundedListView) {
            loadAttendeeTicketsForUid(searchedPhoneUser.user.uid);
          } else if (isRequestListView) {
            loadPendingRequestsTicketsForUid(searchedPhoneUser.user.uid);
          } else {
            loadRefundedTicketsForUid(searchedPhoneUser.user.uid);
          }
        }
      }
    },
    [
      searchMode,
      isRequestListView,
      isRefundedListView,
      loadAttendeeSearchResultsThrottled,
      loadAttendeeTicketsForUid,
      loadPendingRequestsSearchResultsThrottled,
      loadPendingRequestsTicketsForUid,
      loadRefundedSearchResultsThrottled,
      loadRefundedTicketsForUid,
      product.createdBy,
      followingAccountData,
    ]
  );

  const handleLoadMore = useCallback(() => {
    if (searchTerm.trim() === "") {
      if (!isRequestListView && !isRefundedListView) {
        loadMoreAttendeeUsers();
      } else if (isRequestListView) {
        loadMorePendingRequestsUsers();
      } else {
        loadMoreRefundedUsers();
      }
    }
  }, [
    isRequestListView,
    isRefundedListView,
    searchTerm,
    loadMoreAttendeeUsers,
    loadMorePendingRequestsUsers,
    loadMoreRefundedUsers,
  ]);

  const renderItem = useCallback(
    (item: TicketV2) => (
      <ProfileTicketItem
        ticket={item}
        event={product}
        allGuestAccountData={allGuestAccountData}
        isOrganizer={false}
        userTicket={userTicket}
        checkInGuests
        setProfilePreviewSelected={setProfilePreviewSelected}
        setIsEventRoleModalOpen={() => {}}
        setRoleOrganizerData={() => {}}
        setRoleOrganizerTicketItem={() => {}}
        onRefundPress={(user) =>
          setRefundUserItem({ user: user, ticket: item })
        }
        loadingTicketAction={loadingOrderAction}
        setLoadingTicketAction={setLoadingOrderAction}
      />
    ),
    [
      product,
      allGuestAccountData,
      userTicket,
      loadingOrderAction,
      setLoadingOrderAction,
      setProfilePreviewSelected,
    ]
  );

  const isListLoading = isRequestListView
    ? isPendingRequestsLoading
    : isRefundedListView
    ? isRefundedLoading
    : isAttendeeLoading;
  const isListFinished = isRequestListView
    ? isPendingRequestsFinished
    : isRefundedListView
    ? isRefundedFinished
    : isAttendeeFinished;

  const emptyState = (
    <EmptyStateFlatlist
      searchTerm={searchTerm}
      isLoading={isListLoading}
      containerStyles={{ paddingTop: 120 }}
      noResultsDescription={
        searchMode === SearchBoxContactsMode.PHONE
          ? searchByPhoneNumberNoResults(searchTerm)
          : "Check for typos, search for something else, or switch to phone search."
      }
      nonSearchEmptyView={
        <EmptyStateButton
          title={
            listValue === 0
              ? "No Orders"
              : isRequestListView
              ? "No Requests"
              : "No Refunds"
          }
          description={
            listValue === 0
              ? "Your orders will show up here"
              : isRequestListView
              ? "Requested orders will show up here"
              : "Refunded orders will show up here"
          }
          icon={
            <Icon
              icon="ion:share"
              height={70}
              style={{ color: Colors.GRAY1 }}
            />
          }
          iconBox={109.5}
          containerStyles={{ paddingTop: 120 }}
        />
      }
    />
  );

  const listPanelConfig = useMemo(
    () => ({
      listValue,
      setListValue,
      tabLabels: ["Orders"]
        .concat(hasRequestTicket ? ["Requested"] : [])
        .concat(["Refunded"]),
      tabNumbers: [totalGuests]
        .concat(hasRequestTicket ? [totalRequested] : [])
        .concat([totalRefunded]),
      searchTerm,
      onSearch: handleSearch,
      searchMode,
      setSearchMode,
      gridFlexSections: TICKET_FLEX_SECTIONS,
      gridSectionTitles: ["Name", "Date", "Order Option", "Sale"],
    }),
    [
      listValue,
      hasRequestTicket,
      totalGuests,
      totalRequested,
      totalRefunded,
      searchTerm,
      handleSearch,
      searchMode,
    ]
  );

  const listFooter =
    isListLoading && !isListFinished ? (
      <div className="Centering" style={{ paddingBlock: 14 }}>
        <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
      </div>
    ) : (
      <div style={{ height: 40 }} />
    );

  return (
    <TabPanel value={dashboardValue} index={0}>
      <div className="ColumnNormal" style={{ gap: 30 }}>
        {campaign && campaignDetail ? (
          <StandardBorderedContainer containerStyles={styles.sectionContainer}>
            <AutomationItemPreview
              campaignItem={campaign}
              campaignDetail={campaignDetail}
              onEventDashboard
            />
          </StandardBorderedContainer>
        ) : null}
        <DashboardListPanel
          config={listPanelConfig}
          event={product}
          dashboardVisiblity={2} // modify later if we have organizer roles
          tickets={currentOrdersToShow}
          renderItem={renderItem}
          onLoadMore={handleLoadMore}
          emptyState={emptyState}
          listFooter={listFooter}
          showScanButton={false}
        />
      </div>
      {refundUserItem ? (
        <EventRefundModal
          event={product}
          setModalVisible={() => setRefundUserItem(undefined)}
          ticket={refundUserItem.ticket}
          userData={refundUserItem.user}
        />
      ) : null}
      {profilePreviewSelected ? (
        <FullProfilePreviewModal
          profileSelected={profilePreviewSelected}
          setProfileSelected={setProfilePreviewSelected}
          event={product}
          userTicket={userTicket}
        />
      ) : null}
    </TabPanel>
  );
};
